import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Classes,
  Colors,
  Icon,
  InputGroup,
  Menu,
  MenuItem,
  Position,
} from "@blueprintjs/core";
import debounce from "lodash/debounce";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { usePagination } from "@/components/helpers/custom-hooks/use-pagination";
import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { ClientPriceChangingDialog } from "@/components/pages_logged_in/services/ClientPriceChangingDialog";
import { PaginationButton } from "@/components/pieces/form/PaginationButton";
import { RoundedIconButton } from "@/components/pieces/form/RoundedIconButton";
import { WithLoader } from "@/components/pieces/loading/WithLoader";
import { SimpleTable } from "@/components/pieces/table/SimpleTable";
import { Avatar } from "@/components/pieces/users/Avatar";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  ProductDetailQuery,
  SetCustomPriceOptionMutationVariables,
  useCoachCustomersQuery,
  useSetCustomPriceOptionMutation,
} from "@/graphql";
import { CoachClient } from "@/types/coaches";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";
import { formatCredits } from "@/utils/string";

import { PriceUpdateConfirmationStep } from "./utils";

const PAGE_SIZE = 10;

const getCoachClientColumn = (
  onClick: (client: CoachClient) => void,
): {
  id: string;
  header: ReactNode;
  renderCell?: (item: CoachClient) => ReactElement;
}[] => [
  {
    id: "clients",
    header: <th align="left">Clients</th>,
    renderCell: (client) => {
      return (
        <td align="left">
          <Flex alignItems="center">
            <Tooltip content={client.email} placement="bottom">
              <Avatar user={client} size={20} />
            </Tooltip>
            <Box css={{ marginLeft: 6.75 }}>{client.name || client.email}</Box>
          </Flex>
        </td>
      );
    },
  },
  {
    id: "last-purchase",
    header: <th align="left">Last Purchase</th>,
    renderCell: (client) => {
      return (
        <td align="left">
          <Box textAlign="left" color={SUBTEXT_COLOR}>
            {client.lastPurchaseAt
              ? formatDate(client.lastPurchaseAt, "MMM d 'at' h:mma z")
              : "-"}
          </Box>
        </td>
      );
    },
  },
  {
    id: "price-for-client",
    header: <th align="right">Price For Client</th>,
    renderCell: (client) => {
      const { currentPriceOption } = client || {};
      const { coachCreditValue } = currentPriceOption || {};
      return (
        <td align="right">
          {!client.currentPriceOption ? (
            <Box textAlign="right" fontStyle="italic">
              Same as default
            </Box>
          ) : (
            <Box display={"inline-flex"}>
              <EmbedSvg src={CreditSVG} height={16} width={16} />
              <Box ml={2} className="dot-line" css={{ display: "inline" }}>
                {formatCredits(client.currentPriceOption?.price)}
              </Box>
              {coachCreditValue && (
                <Box color={SUBTEXT_COLOR} mt={"5px"}>
                  <Icon
                    icon="lightning"
                    color={SUBTEXT_COLOR}
                    style={{ marginRight: 5 }}
                  />
                  You earn {formatCredits(coachCreditValue)}
                </Box>
              )}
            </Box>
          )}
        </td>
      );
    },
  },
  {
    id: "option",
    header: <th style={{ width: 44 }} />,
    renderCell: (client) => (
      <td>
        <OptionCell client={client} onClick={onClick} />
      </td>
    ),
  },
];

interface OptionCellProps {
  client: CoachClient;
  onClick: (client: CoachClient) => void;
}

function OptionCell({ client, onClick }: OptionCellProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover
      content={
        <Menu>
          <MenuItem
            onClick={() => {
              onClick(client);
              setIsOpen(false);
            }}
            text="Change price"
          />
        </Menu>
      }
      canEscapeKeyClose={true}
      position={Position.BOTTOM_RIGHT}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <RoundedIconButton
        backgroundColor={Colors.LIGHT_GRAY5}
        icon="more"
        size={44}
        onClick={() => setIsOpen(!isOpen)}
      />
    </Popover>
  );
}

export const LOADER = (
  <Box
    className={Classes.SKELETON}
    height={64}
    width="100%"
    css={{ marginTop: 12 }}
  />
);

interface ClientSettingsProps {
  coachProduct?: ProductDetailQuery["productById"]["coachProduct"];
  loading: boolean;
}

export function ClientSettings({ coachProduct, loading }: ClientSettingsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const productId = coachProduct?.productId || "";

  const [selectedClient, setSelectedClient] = useState<CoachClient>();

  const [setCustomPriceOption] = useSetCustomPriceOptionMutation({
    onCompleted: () => {
      setDialogStep(PriceUpdateConfirmationStep.SUCCESS);
      refetch();
    },
    onError: () => {
      setDialogStep(PriceUpdateConfirmationStep.ERROR);
    },
  });

  const [dialogStep, setDialogStep] = useState(
    PriceUpdateConfirmationStep.INITIAL,
  );

  const [searchText, setSearchText] = useState("");

  const { data, refetch } = useCoachCustomersQuery({
    variables: {
      productId,
      excludeWorkspaceCustomers: false,
      includeCustomersOfOtherProducts: true,
    },
    skip: !productId,
  });

  const customers = ensureNonNullArray(data?.coachCustomers);

  const onCoachClientClick = (client: CoachClient) => {
    setIsDialogOpen(true);
    setSelectedClient(client);
  };

  const coachClientColumns = useMemo(
    () => getCoachClientColumn(onCoachClientClick),
    [onCoachClientClick],
  );

  const handlePriceUpdateConfirmed = (
    data: SetCustomPriceOptionMutationVariables,
  ) => {
    setCustomPriceOption({
      variables: data,
    });
    setDialogStep(PriceUpdateConfirmationStep.SUCCESS);
  };

  const handleDialogClose = () => {
    setDialogStep(PriceUpdateConfirmationStep.INITIAL);
    setIsDialogOpen(false);
  };

  const [filteredCoachClients, setFilteredCoachClients] = useState<
    CoachClient[]
  >([]);

  const {
    startIndex,
    endIndex,
    back: backPage,
    next: nextPage,
    setCurrentPage,
  } = usePagination({
    pageSize: PAGE_SIZE,
    total: filteredCoachClients.length,
  });

  const filterCoachClients = useCallback(
    (searchText: string) =>
      customers.filter(
        (client) =>
          (client.name &&
            client.name.trim().toUpperCase().includes(searchText)) ||
          (client.email &&
            client.email.trim().toUpperCase().includes(searchText)),
      ),
    [customers],
  );

  const onSearch = useCallback(
    debounce((text) => {
      const searchStr = text.trim().toUpperCase();
      const filtered = filterCoachClients(searchStr);
      setFilteredCoachClients(filtered);
      setSearchText(searchStr);
      setCurrentPage(1);
    }, 500),
    [customers],
  );

  useEffect(() => {
    setFilteredCoachClients(filterCoachClients(searchText));
  }, [customers]);

  const displayedCoachClients = useMemo(
    () => filteredCoachClients.slice(startIndex, endIndex + 1),
    [filteredCoachClients, startIndex, endIndex],
  );

  return (
    <Box>
      <Flex
        mb={2}
        justifyContent="space-between"
        fontSize={20}
        fontWeight={600}
      >
        <Box>Client Settings</Box>
        <Box>
          <InputGroup
            autoFocus={false}
            leftIcon="search"
            placeholder="Filter Users..."
            fill={true}
            large={true}
            color="blue"
            onChange={(e) => {
              onSearch(e.target.value);
            }}
            style={{ borderRadius: 6 }}
          />
        </Box>
      </Flex>
      <WithLoader loading={loading} loader={LOADER}>
        <Box mt={1}>
          <SimpleTable
            showCheckBox={false}
            columns={coachClientColumns}
            rows={displayedCoachClients}
            selectedIds={[]}
            checkedAll={false}
            onCheckRow={() => {}}
            onCheckAll={() => {}}
            emptyText="No settings available"
          />
          {filteredCoachClients.length > 0 && (
            <Flex justifyContent="center" alignItems="center" pt={12}>
              <PaginationButton
                icon="chevron-left"
                style={{ opacity: 0.3 }}
                onClick={backPage}
              />
              <Box mx={3}>
                {startIndex + 1} - {endIndex + 1} of{" "}
                {filteredCoachClients.length}
              </Box>
              <PaginationButton
                icon="chevron-right"
                style={{ opacity: 0.3 }}
                onClick={nextPage}
              />
            </Flex>
          )}
        </Box>
      </WithLoader>
      {selectedClient && coachProduct && (
        <ClientPriceChangingDialog
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          client={selectedClient}
          coachProduct={coachProduct}
          isLoading={loading}
          step={dialogStep}
          onConfirm={handlePriceUpdateConfirmed}
        />
      )}
    </Box>
  );
}
